import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import DatePicker, { registerLocale } from 'react-datepicker'
import { el, tr } from 'date-fns/locale'
import Button from '../Buttons/Button'
import TextButton from '../Buttons/TextButton'
import Spinner from '../UI/Spinner'
import SettingsLabelText from '../UI/SettingsLabelText'
import useUpdateOrganizationEod from '../../services/organizations/useUpdateOrganizationEod'
import 'react-datepicker/dist/react-datepicker.css'

registerLocale('el', el)
registerLocale('tr', tr)

interface Props {
  organizationId: string
  currentEndDate?: string | null
  onClose: () => void
}

const UpdateEodModal = ({
  organizationId,
  currentEndDate,
  onClose,
}: Props) => {
  const { t, i18n } = useTranslation()
  const { mutate, isLoading } = useUpdateOrganizationEod(organizationId)
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    currentEndDate ? new Date(currentEndDate) : null,
  )

  const handleSubmit = () => {
    if (!selectedDate) return
    // Full ISO-8601 rather than a bare date: the backend field is a
    // DateTimeField, and this pins the instant to the midnight the operator
    // actually picked in their own timezone instead of the server's.
    mutate(selectedDate.toISOString(), {
      onSuccess: () => onClose(),
    })
  }

  return (
    <div className='overflow-y-auto overflow-x-hidden fixed right-0 left-0 top-0 z-50 flex justify-center items-center md:inset-0 h-full bg-[rgba(0,0,0,0.1)]'>
      <div className='relative px-4 w-full h-auto max-w-lg'>
        <div className='relative bg-white rounded-20px shadow p-6'>
          <h2 className='font-bold text-2xl text-textBlack'>
            {t('updateEod')}
          </h2>
          <p className='text-base text-lightGray mt-2'>
            {t('updateEodDescription')}
          </p>

          <div className='mt-6'>
            <SettingsLabelText text={t('subscriptionEod')} />
            <DatePicker
              locale={
                i18n.language === 'el'
                  ? 'el'
                  : i18n.language === 'tr'
                    ? 'tr'
                    : 'en'
              }
              dateFormat='dd/MM/yyyy'
              calendarClassName='bg-white'
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              className='w-full border-[1px] border-solid border-borderGray rounded-lg px-4 h-[3rem] focus:outline-none'
            />
          </div>

          <div className='flex items-center justify-end gap-6 mt-8'>
            <TextButton
              variant='secondary'
              label={t('cancel')}
              disabled={isLoading}
              onClickHandler={onClose}
            />
            {isLoading ? (
              <Spinner spinner='form' color='#0C75FF' />
            ) : (
              <Button
                type='button'
                text={t('save')}
                width='w-max'
                disabled={!selectedDate}
                onClickHandler={handleSubmit}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default UpdateEodModal
